/**
 * Room phone join (no PACS login). Reached by scanning the QR on the room computer. The phone becomes the room's
 * audio: speaker on, mic live, next to the patient and the tech. It sees the instrument and the remote doctor, and
 * only the patient's initials and accession tail.
 */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Box, Button, Container, Paper, Stack, Typography } from "@mui/material";
import {
  Aperture,
  DeviceMobile,
  LockSimpleOpen,
  Microphone,
  MicrophoneSlash,
  SignOut,
  SpeakerHigh,
  UserCircle,
} from "@phosphor-icons/react";
import { ConnectionDots, Mono } from "../ui";
import { caps, tones } from "../theme";
import { env } from "../env";
import { SessionCall, SessionState } from "../SessionCall";

interface PassInfo {
  role?: "room" | "reader";
  patient: string;
  instrument: string;
  expiresAt: string;
  issuedBy?: string | null;
  full: boolean;
}

const KEY = "moyae-pacs-join";

async function api<T>(
  method: string,
  path: string,
  token?: string,
  body?: unknown
): Promise<{ ok: boolean; status: number; data: T & { error?: string } }> {
  const res = await fetch(`${env.apiUrl}${path}`, {
    method,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  return { ok: res.ok, status: res.status, data };
}

/** Keep the screen on while in the call; a locked phone drops the call (an iOS limit). */
function useWakeLock(active: boolean) {
  useEffect(() => {
    if (!active || !("wakeLock" in navigator)) return;
    let lock: { release: () => Promise<void> } | null = null;
    const take = () =>
      (
        navigator as unknown as { wakeLock: { request: (t: string) => Promise<{ release: () => Promise<void> }> } }
      ).wakeLock
        .request("screen")
        .then((l) => (lock = l))
        .catch(() => undefined);
    take();
    const onVis = () => document.visibilityState === "visible" && take();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      lock?.release().catch(() => undefined);
    };
  }, [active]);
}

export default function Join() {
  const { pass } = useParams();
  const [info, setInfo] = useState<PassInfo | null>(null);
  const [error, setError] = useState("");
  const [token, setToken] = useState<string>(() => {
    try {
      return sessionStorage.getItem(`${KEY}:${pass}`) ?? "";
    } catch {
      return "";
    }
  });
  const [state, setState] = useState<SessionState | null>(null);
  const joining = useRef(false);
  useWakeLock(!!token && state?.status === "live");

  useEffect(() => {
    if (token) return;
    api<PassInfo>("GET", `/j/${pass}`).then((r) =>
      r.ok ? setInfo(r.data) : setError(r.data.error ?? "This join code is not valid.")
    );
  }, [pass, token]);

  useEffect(() => {
    if (!token) return;
    let stop = false;
    const poll = async () => {
      const r = await api<SessionState>("GET", "/join/state", token);
      if (stop) return;
      if (r.ok) setState(r.data);
      else {
        setError(r.data.error ?? "You are no longer in this session.");
        setState((s) => (s ? { ...s, status: "ended" } : s));
      }
    };
    poll();
    const iv = setInterval(poll, 3000);
    return () => {
      stop = true;
      clearInterval(iv);
    };
  }, [token]);

  const join = async () => {
    if (joining.current) return;
    joining.current = true;
    try {
      // Ask for the mic inside the tap so iOS allows it; SessionCall reopens it to publish.
      const probe = await navigator.mediaDevices.getUserMedia({ audio: true }).catch(() => null);
      probe?.getTracks().forEach((t) => t.stop());
      if (!probe)
        return setError(
          info?.role === "reader"
            ? "Microphone access is required so the room can hear you. Allow microphone access in the browser, then try again."
            : "Microphone access is required so the remote reader can hear the room. Allow microphone access in the browser, then try again."
        );
      const r = await api<{ participantToken: string }>("POST", `/j/${pass}/redeem`, undefined, {});
      if (!r.ok)
        return setError(r.data.error ?? "Could not join the session. Try again, or ask the technician for a new code.");
      try {
        sessionStorage.setItem(`${KEY}:${pass}`, r.data.participantToken);
      } catch {
        /* this tab only */
      }
      setToken(r.data.participantToken);
    } finally {
      joining.current = false;
    }
  };

  const takeAudio = () => api("POST", "/join/room-audio", token);
  const leave = async () => {
    await api("POST", "/join/leave", token);
    try {
      sessionStorage.removeItem(`${KEY}:${pass}`);
    } catch {
      /* fine */
    }
    setToken("");
    setState(null);
    setError("You left the session.");
  };

  const live = state?.status === "live";
  const iAmRoomAudio = !!state?.me?.audio;
  const tip = (icon: React.ReactNode, text: string) => (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box sx={{ display: "flex", color: "primary.main", flexShrink: 0 }}>{icon}</Box>
      <Typography variant="body2" color="text.secondary">
        {text}
      </Typography>
    </Stack>
  );

  return (
    <Container
      maxWidth="sm"
      sx={{
        px: 2,
        pt: "max(16px, env(safe-area-inset-top))",
        pb: "max(24px, env(safe-area-inset-bottom))",
        minHeight: "100dvh",
      }}
    >
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 2.5 }}>
        <Typography sx={{ ...caps, fontSize: 14, fontWeight: 500, letterSpacing: "0.45em", color: "#fff" }}>
          Jupiter
        </Typography>
        <Typography sx={{ ...caps, fontSize: 9, color: "text.secondary" }}>Remote exam</Typography>
      </Stack>
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {!token && info && (
        <Stack spacing={2.5}>
          <Paper sx={{ p: 2.5, position: "relative", overflow: "hidden" }}>
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                right: -80,
                top: -110,
                width: 240,
                height: 240,
                borderRadius: "50%",
                background: "radial-gradient(circle, rgba(217,162,95,0.16), transparent 65%)",
              }}
            />
            <Typography variant="overline" sx={{ color: "primary.main" }}>
              You are joining
            </Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 500, lineHeight: 1.3, position: "relative" }}>
              {info.instrument}
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              Patient <Mono sx={{ color: "text.primary", fontSize: 16 }}>{info.patient}</Mono>
            </Typography>
            <ConnectionDots
              state="waiting"
              from={<DeviceMobile size={20} />}
              to={<Aperture size={20} />}
              dots={8}
              size={5}
              sx={{ mt: 2.5 }}
            />
          </Paper>
          {info.role === "reader" ? (
            <Stack spacing={1.5} sx={{ px: 0.5 }}>
              <Typography variant="body1">
                You join as the remote reader: you see the slit lamp live, with the room's camera, and the room hears
                you.
              </Typography>
              {tip(<LockSimpleOpen size={22} />, "Keep the screen unlocked while you are in the exam.")}
              {tip(<SpeakerHigh size={22} />, "Headphones help if you are not alone.")}
            </Stack>
          ) : (
            <Stack spacing={1.5} sx={{ px: 0.5 }}>
              <Typography variant="body1">This phone becomes the room's speaker, microphone and camera.</Typography>
              {tip(<UserCircle size={22} />, "Put it near the patient.")}
              {tip(<LockSimpleOpen size={22} />, "Keep the screen unlocked. A locked phone drops the call.")}
              {tip(<SpeakerHigh size={22} />, "Keep the volume up.")}
            </Stack>
          )}
          <Typography variant="body2" color="text.secondary">
            In the session and its record, this device is shown as{" "}
            <b>
              {info.role === "reader" ? "Doctor (invited)" : "Room phone"}, admitted by{" "}
              {info.issuedBy ?? "the staff member who started the session"}
            </b>
            .
          </Typography>
          <Button
            variant="contained"
            size="large"
            fullWidth
            disabled={info.full}
            onClick={join}
            startIcon={<Microphone size={22} weight="regular" />}
            sx={{ minHeight: 60, fontSize: "0.85rem" }}
          >
            {info.full
              ? "This code has already been used"
              : info.role === "reader"
              ? "Join as the doctor"
              : "Join with this phone"}
          </Button>
        </Stack>
      )}
      {token && state && (
        <Stack spacing={2}>
          <Paper
            sx={{
              p: 2,
              borderColor: live && iAmRoomAudio ? "rgba(111,211,223,0.35)" : undefined,
              background:
                live && iAmRoomAudio
                  ? "linear-gradient(135deg, rgba(111,211,223,0.10), rgba(17,19,23,0.9) 60%)"
                  : undefined,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: live && iAmRoomAudio ? tones.live : "text.secondary",
                  border: "1px solid",
                  borderColor: live && iAmRoomAudio ? "rgba(111,211,223,0.5)" : "divider",
                  boxShadow: live && iAmRoomAudio ? "0 0 24px rgba(111,211,223,0.25)" : "none",
                }}
              >
                {live && iAmRoomAudio ? <Microphone size={26} weight="regular" /> : <MicrophoneSlash size={26} />}
              </Box>
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 500, lineHeight: 1.3 }}>
                  {!live
                    ? "Session ended"
                    : state.me?.kind === "remote"
                    ? "You are the remote reader"
                    : iAmRoomAudio
                    ? "This phone has room audio"
                    : "Room audio is on another device"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Patient <Mono sx={{ color: "text.primary" }}>{state.patient.label}</Mono> ·{" "}
                  {live ? "in session" : "session ended"}
                </Typography>
                {live && iAmRoomAudio && state.me?.kind !== "remote" && (
                  <ConnectionDots
                    state="live"
                    from={<DeviceMobile size={16} />}
                    to={<UserCircle size={16} />}
                    label="the remote reader hears this phone"
                    sx={{ mt: 0.75 }}
                  />
                )}
              </Box>
            </Stack>
          </Paper>
          <SessionCall
            state={state}
            token={token}
            onTakeRoomAudio={state.me?.kind === "remote" ? undefined : takeAudio}
          />
          {live && (
            <Button
              color="error"
              variant="outlined"
              size="large"
              fullWidth
              startIcon={<SignOut size={20} />}
              onClick={leave}
              sx={{ minHeight: 56 }}
            >
              Leave
            </Button>
          )}
        </Stack>
      )}
    </Container>
  );
}
